'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { usePlatformSettings } from '@/contexts/PlatformSettingsContext';
import { createClient } from '@/lib/supabase/client';

// All possible operational centres — filtered at render time based on platform settings
const ALL_OPERATIONAL_CENTERS = [
  { label: 'HR Center', href: '/hr-center', icon: 'UserGroupIcon', badge: 4, settingKey: 'centreHrEnabled' },
  { label: 'Payroll Center', href: '/payroll-center', icon: 'BanknotesIcon', badge: 1, settingKey: 'centrePayrollEnabled' },
  { label: 'Shift Scheduling', href: '/shift-scheduling', icon: 'CalendarDaysIcon', badge: 5, settingKey: 'centreShiftSchedulingEnabled' },
  { label: 'Employee Transport', href: '/employee-transport', icon: 'TruckIcon', badge: null, settingKey: 'centreEmployeeTransportEnabled' },
  { label: 'Warehouse Center', href: '#', icon: 'BuildingStorefrontIcon', badge: 7, settingKey: 'centreWarehouseEnabled' },
  { label: 'Clients & Sites', href: '/clients-sites', icon: 'BuildingOfficeIcon', badge: null, settingKey: 'centreClientsSitesEnabled' },
] as const;

const navMenu = {
  overview: [
    { label: 'Platform Dashboard', href: '/platform-dashboard', icon: 'Squares2X2Icon', badge: null },
  ],
  operationalCenters: [
    { label: 'SENTINEL', href: '/sentinel', icon: 'ShieldCheckIcon', badge: null },
  ],
  administration: [
    { label: 'Activity Log', href: '/activity-log', icon: 'ClipboardDocumentListIcon', badge: null },
    { label: 'Approval History', href: '#', icon: 'CheckBadgeIcon', badge: null },
    { label: 'Employee Tax History', href: '#', icon: 'DocumentTextIcon', badge: null },
    { label: 'User Management', href: '#', icon: 'UsersIcon', badge: null },
    { label: 'Tenant Configuration', href: '/tenant-configuration', icon: 'Cog6ToothIcon', badge: null },
    { label: 'Platform Settings', href: '/platform-settings', icon: 'AdjustmentsHorizontalIcon', badge: null },
  ],
};

const breadcrumbMap: Record<string, string> = {
  '/platform-dashboard': 'Platform Dashboard',
  '/sentinel': 'SENTINEL',
  '/hr-center': 'HR Center',
  '/payroll-center': 'Payroll Center',
  '/shift-scheduling': 'Shift Scheduling',
  '/sign-up-login-screen': 'Sign In',
  '/tenant-configuration': 'Tenant Configuration',
  '/platform-settings': 'Platform Settings',
  '/activity-log': 'Activity Log',
  '/clients-sites': 'Clients & Sites',
  '/': 'Platform Dashboard',
};

// Centre metadata used for badge orbit icons
const CENTRE_META: Record<string, { icon: string }> = {
  'HR Center':            { icon: 'UserGroupIcon' },
  'Payroll Center':       { icon: 'BanknotesIcon' },
  'Shift Scheduling':     { icon: 'CalendarDaysIcon' },
  'Employee Transport':   { icon: 'TruckIcon' },
  'Warehouse Center':     { icon: 'BuildingStorefrontIcon' },
  'Clients & Sites':      { icon: 'BuildingOfficeIcon' },
  'SENTINEL':             { icon: 'ShieldCheckIcon' },
};

// Badge ring colour map (matches RoleSlideOver badgeRings)
const BADGE_COLORS: Record<string, string> = {
  gold:     '#F59E0B',
  cyan:     '#00D4FF',
  purple:   '#8B5CF6',
  red:      '#EF4444',
  green:    '#10B981',
  blue:     '#3B82F6',
  indigo:   '#6366F1',
  pink:     '#EC4899',
  rose:     '#F43F5E',
  orange:   '#F97316',
  yellow:   '#EAB308',
  teal:     '#14B8A6',
  sky:      '#0EA5E9',
  lime:     '#84CC16',
  emerald:  '#059669',
  violet:   '#7C3AED',
  fuchsia:  '#D946EF',
  slate:    '#64748B',
  coral:    '#FF6B6B',
  mint:     '#00C9A7',
  lavender: '#A78BFA',
  amber:    '#D97706',
  zinc:     '#71717A',
  stone:    '#78716C',
};

// Compute (x, y) for an icon placed on a circle of given radius
function getIconPosition(index: number, total: number, radius: number) {
  const startDeg = 270;
  const spread = total === 1 ? 0 : Math.min(180, (total - 1) * 36);
  const step = total === 1 ? 0 : spread / (total - 1);
  const deg = startDeg - index * step;
  const rad = (deg * Math.PI) / 180;
  return { x: radius * Math.cos(rad), y: radius * Math.sin(rad) };
}

// Map from centre name to its platform settings key
const CENTRE_SETTING_KEY: Record<string, string> = {
  'HR Center':          'centreHrEnabled',
  'Payroll Center':     'centrePayrollEnabled',
  'Shift Scheduling':   'centreShiftSchedulingEnabled',
  'Employee Transport': 'centreEmployeeTransportEnabled',
  'Warehouse Center':   'centreWarehouseEnabled',
  'Clients & Sites':    'centreClientsSitesEnabled',
  'SENTINEL':           '__sentinel__', // always visible — no platform settings gate
};

const MAX_VISIBLE_BADGE_ICONS = 5;

interface RoleBadgeData {
  roleName: string;
  initials: string;
  ring: string;
  accessibleCentres: string[]; // centre names with non-None access
}

// Compact badge component rendered in the toolbar
function RoleBadgePill({ badge }: { badge: RoleBadgeData }) {
  const ringColor = BADGE_COLORS[badge.ring] ?? '#00D4FF';
  const badgeSize = 28;
  const orbitRadius = 21;
  const iconSize = 13;
  const containerSize = badgeSize + orbitRadius * 2 + 4;
  const half = containerSize / 2;

  return (
    <div
      className="flex items-center gap-1.5 px-2 py-1 rounded-md"
      title={`Role: ${badge.roleName}`}
      style={{ background: `${ringColor}12`, border: `1px solid ${ringColor}40` }}
    >
      {/* Badge circle with orbiting icons */}
      <div className="relative flex-shrink-0" style={{ width: containerSize, height: containerSize }}>
        {/* Centre circle */}
        <div
          className="rounded-full flex items-center justify-center text-xs font-bold"
          style={{
            width: badgeSize,
            height: badgeSize,
            background: 'var(--card)',
            color: ringColor,
            border: `2px solid ${ringColor}`,
            boxShadow: `0 0 6px ${ringColor}50`,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {badge.initials}
        </div>

        {/* Orbiting centre icons */}
        {badge.accessibleCentres.map((centreName, idx) => {
          const meta = CENTRE_META[centreName];
          if (!meta) return null;
          const pos = getIconPosition(idx, badge.accessibleCentres.length, orbitRadius);
          return (
            <div
              key={`toolbar-badge-icon-${centreName}`}
              title={centreName}
              style={{
                position: 'absolute',
                left: half + pos.x - iconSize / 2,
                top: half - pos.y - iconSize / 2,
                color: ringColor,
                filter: `drop-shadow(0 0 3px ${ringColor}80)`,
              }}
            >
              <Icon name={meta.icon as any} size={iconSize} />
            </div>
          );
        })}
      </div>

      {/* Role label */}
      <span className="text-xs font-semibold hidden lg:block" style={{ color: ringColor }}>
        {badge.roleName}
      </span>
    </div>
  );
}

export default function GlobalToolbar() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { settings: platformSettings } = usePlatformSettings();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profile, setProfile] = useState<{ full_name: string; role_name: string } | null>(null);
  const [roleBadge, setRoleBadge] = useState<RoleBadgeData | null>(null);
  const [overflowTooltipVisible, setOverflowTooltipVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Filter operational centres based on platform settings
  const visibleOperationalCenters = ALL_OPERATIONAL_CENTERS.filter(
    (item) => !!(platformSettings[item.settingKey as keyof typeof platformSettings])
  );

  // Derive badge centres: only those accessible by the role AND enabled in platform settings
  const badgeCentres = roleBadge
    ? roleBadge.accessibleCentres.filter((name) => {
        const key = CENTRE_SETTING_KEY[name];
        if (!key) return false;
        if (key === '__sentinel__') return true; // SENTINEL always shown
        return !!(platformSettings[key as keyof typeof platformSettings]);
      })
    : [];

  const visibleBadgeCentres = badgeCentres.slice(0, MAX_VISIBLE_BADGE_ICONS);
  const overflowCentres = badgeCentres.slice(MAX_VISIBLE_BADGE_ICONS);
  const overflowCount = overflowCentres.length;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    supabase
      .from('user_profiles')
      .select('full_name')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setProfile({ full_name: data.full_name || user.email || '', role_name: 'Platform Owner' });
        } else {
          setProfile({ full_name: user.email || '', role_name: 'Platform Owner' });
        }
      });
  }, [user]);

  // Fetch the current user's role badge from sentinel_role_members + sentinel_roles + sentinel_permissions
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();

    const loadBadge = async () => {
      try {
        // 1. Find the user's role membership
        const { data: memberData, error: memberError } = await supabase
          .from('sentinel_role_members')
          .select('role_id')
          .eq('user_id', user.id)
          .limit(1)
          .single();

        let roleId: string | null = null;

        if (!memberError && memberData?.role_id) {
          roleId = memberData.role_id;
        } else {
          // Fallback: if no membership row, try to find the Platform Owner role for this user's company
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('company_id')
            .eq('id', user.id)
            .single();

          if (profileData?.company_id) {
            const { data: ownerRole } = await supabase
              .from('sentinel_roles')
              .select('id')
              .eq('company_id', profileData.company_id)
              .eq('name', 'Platform Owner')
              .single();
            if (ownerRole?.id) roleId = ownerRole.id;
          }
        }

        if (!roleId) return;

        // 2. Fetch the role details
        const { data: roleData, error: roleError } = await supabase
          .from('sentinel_roles')
          .select('id, name, initials, ring')
          .eq('id', roleId)
          .single();

        if (roleError || !roleData) return;

        // 3. Fetch permissions for this role
        const { data: permData } = await supabase
          .from('sentinel_permissions')
          .select('centre_name, access_level')
          .eq('role_id', roleId);

        const accessibleCentres = (permData ?? [])
          .filter((p: any) => p.access_level && p.access_level !== 'None')
          .map((p: any) => p.centre_name as string);

        setRoleBadge({
          roleName: roleData.name,
          initials: roleData.initials || roleData.name.slice(0, 2).toUpperCase(),
          ring: roleData.ring || 'cyan',
          accessibleCentres,
        });
      } catch {
        // silently ignore — badge is non-critical
      }
    };

    loadBadge();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
    } catch {
      router.push('/sign-up-login-screen');
    }
  };

  const displayName = profile?.full_name || user?.email || '';
  const displayEmail = user?.email || '';
  const initials = displayName
    ? displayName.split(' ').slice(0, 2).map((w: string) => w[0]?.toUpperCase() ?? '').join('')
    : displayEmail.slice(0, 2).toUpperCase();

  const currentBreadcrumb = breadcrumbMap[pathname] || 'Dashboard';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center px-4 gap-3"
      style={{ background: 'var(--card)', borderBottom: '1px solid var(--border)' }}>

      {/* Logo + Menu Trigger */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted transition-colors duration-150"
        >
          <AppLogo size={28} />
          <span className="text-primary font-bold text-sm tracking-widest hidden sm:block">CATALYST</span>
          <Icon name="ChevronDownIcon" size={14} className="text-muted-foreground" />
        </button>

        {/* Mega Menu */}
        {menuOpen && (
          <div className="absolute top-full left-0 mt-2 w-[680px] card-elevated rounded-xl shadow-2xl animate-fade-in z-50 overflow-hidden">
            <div className="grid grid-cols-3 gap-0 p-4">
              {/* Overview */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3 px-2">Overview</p>
                {navMenu.overview.map((item) => (
                  <Link
                    key={`menu-overview-${item.label}`}
                    href={item.href === '/platform-dashboard' && pathname === '/' ? '/' : item.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted transition-colors duration-150 group"
                  >
                    <Icon name={item.icon as any} size={16} className="text-primary" />
                    <span className="text-sm text-card-foreground group-hover:text-primary transition-colors">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-semibold">{item.badge}</span>
                    )}
                  </Link>
                ))}
              </div>

              {/* Operational Centers — only enabled ones */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3 px-2">Operational Centers</p>
                {/* SENTINEL — always shown at top of Operational Centers */}
                {navMenu.operationalCenters.map((item) => (
                  <Link
                    key={`menu-sentinel-${item.label}`}
                    href={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted transition-colors duration-150 group"
                  >
                    <Icon name={item.icon as any} size={16} className="text-primary group-hover:text-primary transition-colors" />
                    <span className="text-sm text-card-foreground group-hover:text-primary transition-colors font-semibold">{item.label}</span>
                  </Link>
                ))}
                {visibleOperationalCenters.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-2 py-2 italic">No centres enabled</p>
                ) : (
                  visibleOperationalCenters.map((item) => (
                    <Link
                      key={`menu-ops-${item.label}`}
                      href={item.href}
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted transition-colors duration-150 group"
                    >
                      <Icon name={item.icon as any} size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                      <span className="text-sm text-card-foreground group-hover:text-primary transition-colors">{item.label}</span>
                      {item.badge && (
                        <span className="ml-auto text-xs bg-color-red text-white px-1.5 py-0.5 rounded-full font-semibold">{item.badge}</span>
                      )}
                    </Link>
                  ))
                )}
              </div>

              {/* Administration */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3 px-2">Administration</p>
                {navMenu.administration.map((item) => (
                  <Link
                    key={`menu-admin-${item.label}`}
                    href={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-muted transition-colors duration-150 group"
                  >
                    <Icon name={item.icon as any} size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                    <span className="text-sm text-card-foreground group-hover:text-primary transition-colors">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Menu Footer */}
            <div className="px-4 py-2.5 border-t border-border flex items-center justify-between"
              style={{ background: 'var(--background)' }}>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-color-green inline-block"></span>
                <span className="text-xs text-muted-foreground">All systems operational</span>
              </div>
              <span className="text-xs text-muted-foreground font-mono">CATALYST v2.0.0</span>
            </div>
          </div>
        )}
      </div>

      {/* Breadcrumb Pill */}
      <div className="hidden md:flex items-center">
        <span className="text-xs px-2.5 py-1 rounded-full font-medium"
          style={{ background: 'rgba(0,212,255,0.1)', color: 'var(--primary)', border: '1px solid rgba(0,212,255,0.2)' }}>
          {currentBreadcrumb}
        </span>
      </div>

      {/* Global Search */}
      <div className="flex-1 max-w-sm mx-auto">
        <div className="relative">
          <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search... (⌘K)"
            className="w-full pl-8 pr-4 py-1.5 text-sm rounded-md outline-none focus:ring-1 focus:ring-primary transition-all"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
          />
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-1 ml-auto">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {theme === 'dark' ? (
            <Icon name="SunIcon" size={18} className="text-muted-foreground" />
          ) : (
            <Icon name="MoonIcon" size={18} className="text-muted-foreground" />
          )}
        </button>
        <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors">
          <Icon name="QuestionMarkCircleIcon" size={18} className="text-muted-foreground" />
        </button>
        <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors">
          <Icon name="ExclamationTriangleIcon" size={18} className="text-color-amber" />
        </button>
        <button className="relative w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors">
          <Icon name="BellIcon" size={18} className="text-muted-foreground" />
          <span className="absolute top-1 right-1 w-4 h-4 rounded-full text-xs flex items-center justify-center font-bold"
            style={{ background: 'var(--color-red)', color: 'white', fontSize: '10px' }}>3</span>
        </button>

                {/* User Menu */}
        <div className="relative ml-1" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-muted transition-colors"
          >
            {/* Avatar — plain or with role badge ring + orbiting icons */}
            {roleBadge ? (() => {
              const ringColor = BADGE_COLORS[roleBadge.ring] ?? '#00D4FF';
              const badgeSize = 28;
              const orbitRadius = 21;
              const iconSize = 13;
              const containerSize = badgeSize + orbitRadius * 2 + 4;
              const half = containerSize / 2;
              return (
                <div className="relative flex-shrink-0" style={{ width: containerSize, height: containerSize }}>
                  {/* Avatar circle with role ring */}
                  <div
                    className="rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      width: badgeSize,
                      height: badgeSize,
                      background: 'linear-gradient(135deg, var(--primary), var(--accent))',
                      color: 'var(--primary-foreground)',
                      border: `2px solid ${ringColor}`,
                      boxShadow: `0 0 6px ${ringColor}60`,
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    {initials}
                  </div>
                  {/* Orbiting centre icons — only enabled centres, max 5 */}
                  {visibleBadgeCentres.map((centreName, idx) => {
                    const meta = CENTRE_META[centreName];
                    if (!meta) return null;
                    const pos = getIconPosition(idx, visibleBadgeCentres.length + (overflowCount > 0 ? 1 : 0), orbitRadius);
                    return (
                      <div
                        key={`toolbar-avatar-icon-${centreName}`}
                        title={centreName}
                        style={{
                          position: 'absolute',
                          left: half + pos.x - iconSize / 2,
                          top: half - pos.y - iconSize / 2,
                          color: ringColor,
                          filter: `drop-shadow(0 0 3px ${ringColor}80)`,
                        }}
                      >
                        <Icon name={meta.icon as any} size={iconSize} />
                      </div>
                    );
                  })}
                  {/* +N overflow badge */}
                  {overflowCount > 0 && (() => {
                    const overflowIdx = visibleBadgeCentres.length;
                    const totalSlots = visibleBadgeCentres.length + 1;
                    const pos = getIconPosition(overflowIdx, totalSlots, orbitRadius);
                    return (
                      <div
                        key="toolbar-avatar-overflow"
                        onMouseEnter={() => setOverflowTooltipVisible(true)}
                        onMouseLeave={() => setOverflowTooltipVisible(false)}
                        style={{
                          position: 'absolute',
                          left: half + pos.x - 8,
                          top: half - pos.y - 7,
                          cursor: 'default',
                        }}
                      >
                        <div
                          className="flex items-center justify-center rounded-full text-white font-bold"
                          style={{
                            width: 16,
                            height: 14,
                            background: ringColor,
                            fontSize: '8px',
                            boxShadow: `0 0 4px ${ringColor}80`,
                          }}
                        >
                          +{overflowCount}
                        </div>
                        {overflowTooltipVisible && (
                          <div
                            className="absolute z-50 rounded-md shadow-lg py-1.5 px-2.5 text-xs whitespace-nowrap"
                            style={{
                              bottom: '120%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: 'var(--card)',
                              border: `1px solid ${ringColor}60`,
                              color: 'var(--foreground)',
                              minWidth: '120px',
                            }}
                          >
                            <p className="font-semibold mb-1" style={{ color: ringColor }}>More centres:</p>
                            {overflowCentres.map((name) => (
                              <p key={name} className="text-muted-foreground">{name}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })() : (
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))', color: 'var(--primary-foreground)' }}>
                {initials}
              </div>
            )}
            {/* Name + role text */}
            <div className="hidden lg:flex flex-col items-start leading-tight">
              <span className="text-xs font-semibold text-card-foreground whitespace-nowrap max-w-[120px] truncate">
                {displayName || '—'}
              </span>
              <span className="text-xs whitespace-nowrap" style={{ color: roleBadge ? (BADGE_COLORS[roleBadge.ring] ?? '#00D4FF') : 'var(--muted-foreground)', fontSize: '10px' }}>
                {roleBadge ? roleBadge.roleName : (profile?.role_name || 'Platform Owner')}
              </span>
            </div>
            <Icon name="ChevronDownIcon" size={12} className="text-muted-foreground hidden lg:block" />
          </button>

          {userMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-52 card-elevated rounded-xl shadow-2xl animate-fade-in z-50 py-1">
              <div className="px-3 py-2 border-b border-border">
                <p className="text-sm font-semibold">{displayName || '—'}</p>
                <p className="text-xs text-muted-foreground">{displayEmail}</p>
                {roleBadge && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{
                        border: `1.5px solid ${BADGE_COLORS[roleBadge.ring] ?? '#00D4FF'}`,
                        color: BADGE_COLORS[roleBadge.ring] ?? '#00D4FF',
                        fontSize: '8px',
                      }}
                    >
                      {roleBadge.initials}
                    </div>
                    <span className="text-xs font-medium" style={{ color: BADGE_COLORS[roleBadge.ring] ?? '#00D4FF' }}>
                      {roleBadge.roleName}
                    </span>
                  </div>
                )}
              </div>
              {[
                { label: 'My Profile', icon: 'UserIcon' },
                { label: 'Account Settings', icon: 'Cog6ToothIcon' },
                { label: 'Platform Settings', icon: 'AdjustmentsHorizontalIcon' },
              ].map((item) => (
                <button key={`user-menu-${item.label}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-muted transition-colors text-left">
                  <Icon name={item.icon as any} size={14} className="text-muted-foreground" />
                  {item.label}
                </button>
              ))}
              <div className="border-t border-border mt-1">
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-muted transition-colors text-color-red text-left"
                >
                  <Icon name="ArrowRightOnRectangleIcon" size={14} className="text-color-red" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}